import axios from 'axios';
import { clearAuthCookies, getCookie, setAuthCookies } from './authUtils';
import { hasInvalidJwtToken } from './axiosUtils';
import { ERROR_MAP } from './api/errorMap';

const instance = axios.create({
  simpleURL: process.env.REACT_APP_CDN_URL,
  baseURL: process.env.REACT_APP_API_URL + 'api/',
  headers: { Authorization: 'Bearer ' + getCookie('token') },
});

export const usersInstance = axios.create({
  simpleURL: process.env.REACT_APP_USERS_URL,
  baseURL: process.env.REACT_APP_USERS_URL + 'api/',
  headers: { Authorization: 'Bearer ' + getCookie('token') },
});

export const logsInstance = axios.create({
  // simpleURL: process.env.REACT_APP_API_URL,
  baseURL: process.env.REACT_APP_LOGS_URL + 'api/',
  headers: { Authorization: 'Bearer ' + getCookie('token') },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const TWO_MINUTES_IN_MS = 2 * 60 * 1000;
const configObject = async (config) => {
  config.headers['Authorization'] = 'Bearer ' + getCookie('token');

  const tokenExpiration = getCookie('expiresAt');

  if (tokenExpiration) {
    const toExpire = new Date(tokenExpiration).getTime() - new Date().getTime();
    const refreshToken = getCookie('refreshToken');

    if (toExpire <= TWO_MINUTES_IN_MS && !isRefreshing) {
      isRefreshing = true;
      const { data } = await axios.post(
        process.env.REACT_APP_USERS_URL + 'api/token/refresh',
        { refresh_token: refreshToken }
      );

      setAuthCookies({
        token: data.token,
        refresh_token: data.refresh_token,
        expires_at: data.expires_at,
      });

      instance.defaults.headers.common['Authorization'] =
        'Bearer ' + data.token;
      usersInstance.defaults.headers.common['Authorization'] =
        'Bearer ' + data.token;
      logsInstance.defaults.headers.common['Authorization'] =
        'Bearer ' + data.token;
    }
  }

  return config;
};

instance.interceptors.request.use(configObject);
usersInstance.interceptors.request.use(configObject);
logsInstance.interceptors.request.use(configObject);

let errorObject = (error) => {
  const originalRequest = error.config;
  const status = error?.response?.status;

  if (
    status === 401 &&
    error.response.data.message === ERROR_MAP[401].INVALID_CREDS
  ) {
    return new Promise.reject();
  }

  if (hasInvalidJwtToken(error)) {
    clearAuthCookies();
    window.location.reload();

    return Promise.reject(error);
  }

  if (
    error.response.status === 401 &&
    (error?.response?.config?.url || '').endsWith('/api/token/refresh')
  ) {
    clearAuthCookies();

    return Promise.reject(error);
  }

  if ((status === 403 || status === 401) && !originalRequest._retry) {
    if (isRefreshing) {
      return new Promise(function (resolve, reject) {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return instance(originalRequest);
        })
        .catch((err) => err);
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = getCookie('refreshToken');

    return new Promise((resolve, reject) => {
      usersInstance
        .post('token/refresh', { refresh_token: refreshToken })
        .then(({ data }) => {
          setAuthCookies(data);

          instance.defaults.headers.common['Authorization'] =
            'Bearer ' + data.token;
          usersInstance.defaults.headers.common['Authorization'] =
            'Bearer ' + data.token;
          logsInstance.defaults.headers.common['Authorization'] =
            'Bearer ' + data.token;
          originalRequest.headers['Authorization'] = 'Bearer ' + data.token;
          processQueue(null, data.token);
          resolve(instance(originalRequest));
        })
        .catch((err) => {
          processQueue(err, null);
          reject(err);
        })
        .finally(() => (isRefreshing = false));
    });
  }

  return Promise.reject(error);
};

instance.interceptors.response.use((response) => {
  return response;
}, errorObject);

usersInstance.interceptors.response.use((response) => {
  return response;
}, errorObject);

logsInstance.interceptors.response.use((response) => {
  return response;
}, errorObject);

export default instance;
